import React from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';

import { isMobileMedia as getIsMobileMedia } from 'bento-ordering/utils/media';
import { goToCart } from 'bento-ordering/cart/actions';
import { goToOrderSettings } from 'bento-ordering/cart/actions/orderSettings';
import { setCartWorkingCopy } from 'bento-ordering/cart/actions/cartWorkingCopy';

import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import isDineInApplicationVariant from 'bento-ordering/utils/isDineInApplicationVariant';

import AppBar, {
  AppBarCartIconButton,
  AppBarDivider,
  AppBarFulfillmentMethodOptionLink,
  AppBarFulfillmentOptions
} from 'components/AppBar';
import { trackNavigateToCartFromNavbar } from 'bento-ordering/base/actions/tracking';
import { setScrollPosition } from 'bento-ordering/base/actions/scrollManager';
import { getAppBarOwnState } from 'helpers/ownState/AppBarOwnState';
import useOnlineOrderingAppBarState from 'bento-ordering/base/hooks/useOnlineOrderingAppBarState';
import AppBarGroupOrderCTA from 'bento-ordering/group_ordering/components/AppBarGroupOrderCTA';
import { useIsGroupOrderGuestDiner } from 'bento-ordering/group_ordering/hooks/index';
import { useHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';

import { MENU } from 'bento-ordering/menus/constants/entities';

const SHOULD_USE_DESIGN_AND_CUSTOMIZATION_THEME = true;

function AppBarContainer() {
  const {
    accountName,
    logo,
    shouldDisplayCart,
    cartQuantity,
    fulfillmentTimeOrASAPDisplay,
    fulfillmentMethod,
    availableFulfillmentOptions,
    deliveryAddress,
    isClosed,
    shouldDisplayFulfillmentOptions,
    logoDestinationUrl
  } = useOnlineOrderingAppBarState();
  const dispatch = useDispatch();
  const hasInvestorDayFeature = useHasInvestorDayFeatureFlag();

  const handleCartClick = () => {
    dispatch(goToCart());
    trackNavigateToCartFromNavbar();
  };

  const handleFulfillmentMethodClick = (selectedFulfillmentMethod = null) => {
    if (
      selectedFulfillmentMethod &&
      selectedFulfillmentMethod !== fulfillmentMethod
    ) {
      dispatch(
        setCartWorkingCopy({
          online_ordering_fulfillment_method: selectedFulfillmentMethod
        })
      );
    }
    if (selectedFulfillmentMethod) {
      sessionStorage.setItem(
        'selectedFulfillmentMethod',
        selectedFulfillmentMethod
      );
    }
    // Set scroll position to return after closing the modal
    dispatch(setScrollPosition(MENU, window.pageYOffset));
    dispatch(goToOrderSettings());
  };

  const isMobileMedia = getIsMobileMedia();
  const isDineInVariant = isDineInApplicationVariant();
  const shouldDisableFulfillmentOptions = useIsGroupOrderGuestDiner();

  const appFulfillmentOptionsOnClick = shouldDisableFulfillmentOptions
    ? () => {}
    : handleFulfillmentMethodClick;

  return (
    <AppBar
      ownState={{
        appBar: getAppBarOwnState(SHOULD_USE_DESIGN_AND_CUSTOMIZATION_THEME)
      }}
      logo={logo}
      logoLinkPath={logoDestinationUrl}
      name={accountName}
    >
      {!isDineInVariant && (
        <AppBarGroupOrderCTA
          shouldDisplayFulfillmentOptions={shouldDisplayFulfillmentOptions}
        />
      )}
      {/*
          -- WARNING --
          This is just a placeholder we need to figure out what the action
          should be when clicking on Dine-In
        */}
      {isDineInVariant &&
        shouldDisplayFulfillmentOptions &&
        !hasInvestorDayFeature && (
          <Box m={'0 4px 0 0'}>
            <AppBarFulfillmentMethodOptionLink
              isActive
              fulfillmentOptionLabel={'Dine-In'}
              fulfillmentOptionIcon={fulfillmentOptions.DINE_IN}
              dataTestId={fulfillmentOptions.DINE_IN}
            />
          </Box>
        )}

      {!isDineInVariant && !isClosed && shouldDisplayFulfillmentOptions && (
        <AppBarFulfillmentOptions
          deliveryAddress={deliveryAddress}
          fulfillmentTimeOrASAPDisplay={fulfillmentTimeOrASAPDisplay}
          fulfillmentOptions={availableFulfillmentOptions}
          fulfillmentMethod={fulfillmentMethod}
          onClick={appFulfillmentOptionsOnClick}
          shouldDisableFulfillmentOptions={shouldDisableFulfillmentOptions}
        >
          {shouldDisplayCart && <AppBarDivider />}
        </AppBarFulfillmentOptions>
      )}

      {shouldDisplayCart && !isMobileMedia && (
        <>
          {isDineInVariant && shouldDisplayFulfillmentOptions && (
            <AppBarDivider />
          )}
          <AppBarCartIconButton
            onClick={handleCartClick}
            itemsInCart={cartQuantity}
          />
        </>
      )}
    </AppBar>
  );
}

export default AppBarContainer;
